import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";

/**
 * Handles the Emergent Google Auth callback.
 * URL pattern: /admin/auth-callback#session_id=<id>
 *
 * Steps:
 *   1) Parse session_id from URL hash
 *   2) POST /api/auth/session — server resolves email, enforces allowlist, sets httpOnly cookie
 *   3) On success → /admin/dashboard. On 403 → "not authorized" screen. On other errors → /admin/login.
 */
export const AdminAuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      setError({ code: 400, message: "Missing session_id." });
      return;
    }
    (async () => {
      try {
        const r = await adminApi.post("/auth/session", { session_id: sessionId });
        // Clean the hash from the URL bar
        window.history.replaceState(null, "", "/admin/auth-callback");
        navigate("/admin/dashboard", { replace: true, state: { user: r.data } });
      } catch (e) {
        const code = e?.response?.status || 500;
        const detail = e?.response?.data?.detail || "Sign-in failed.";
        setError({ code, message: detail });
      }
    })();
  }, [navigate]);

  if (!error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0A", color: "#FF9933" }} data-testid="auth-callback-loading">
        <p className="font-display uppercase text-xl tracking-[0.3em]">Signing in…</p>
      </div>
    );
  }

  const isForbidden = error.code === 403;
  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "#0A0A0A", color: "#FDFBF7" }} data-testid="auth-callback-error">
      <div className="brutal-card p-7 md:p-10 max-w-md w-full" style={{ background: "#141414", borderColor: isForbidden ? "#E3000F" : "#FF9933" }}>
        <p className="text-xs uppercase font-bold tracking-[0.4em]" style={{ color: isForbidden ? "#E3000F" : "#FF9933" }}>
          {isForbidden ? "ACCESS DENIED" : "SIGN-IN FAILED"}
        </p>
        <h1 className="font-display uppercase leading-[0.9] tracking-tight mt-3" style={{ fontSize: "clamp(1.5rem, 4vw, 2.25rem)" }}>
          {isForbidden ? "Access denied." : "We couldn't sign you in."}
        </h1>
        <p className="text-sm font-medium text-neutral-300 mt-3 mb-6">{isForbidden ? "Only Admins Allowed" : error.message}</p>
        <a href="/" className="brutal-btn" style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }} data-testid="auth-error-back">
          Back to homepage
        </a>
      </div>
    </div>
  );
};

export default AdminAuthCallback;
